/**
 * commentMatching — normalización y matcheo de keywords para la feature
 * "comentario con palabra clave -> DM" (ver COMMENT-RULES-SCHEMA.md §6).
 *
 * MÓDULO COMPARTIDO CON COPIAS BYTE-IDÉNTICAS. Se usa en DOS lugares:
 *   1. src/utils/commentMatching.js         — al GUARDAR keywords desde la UI
 *   2. functions/utils/commentMatching.cjs  — al MATCHEAR en runtime
 *
 * Si divergen, el fallo es SILENCIOSO y caro: la UI guarda "Información", el
 * runtime normaliza a "informacion", nunca matchea, y no hay error en ningún
 * log. Por eso hay un gate de paridad byte a byte en el pre-commit
 * (functions/scripts/check-shared-module-parity.js). Editá SIEMPRE las dos
 * copias juntas.
 *
 * La copia de src/ es .js y no .cjs porque CRA no resuelve .cjs; el contenido
 * es CommonJS igual y webpack hace la interop (mismo patrón que rolesMatrix y
 * timezoneMap).
 *
 * Todo carácter invisible (ZWJ, selector de variación, área de uso privado) se
 * construye por CÓDIGO, nunca pegado literal: este archivo tiene que ser
 * byte-idéntico a su copia, y un carácter invisible en el fuente es imposible
 * de revisar en un diff y trivial de romper al copiar.
 *
 * Funciones PURAS: sin Firestore, sin red, sin dependencias.
 */

// Caracteres que cuentan como "parte de una palabra" para el límite de
// matchKeyword. NO se usa \b de JS: no considera letra a la ñ, así que "año"
// con keyword "año" fallaría. Las vocales acentuadas no hacen falta acá porque
// normalizeCommentText ya las quitó cuando esto corre.
const WORD_CHARS = 'a-z0-9ñ';

// La ñ se PRESERVA (decisión de producto, COMMENT-RULES-SCHEMA.md §6): el
// cliente escribe "diseño" en la UI y espera que matchee "diseño". Normalizarla
// a "diseno" lo obligaría a escribir mal la keyword sin ninguna forma de
// descubrirlo.
//
// Esto choca con el paso de diacríticos: NFD descompone ñ en "n" + U+0303
// (tilde combinante), que cae dentro del rango que se elimina. Sin protegerla,
// "diseño" saldría "diseno" y la decisión de arriba quedaría rota en silencio.
// Se sustituye por un carácter del Área de Uso Privado antes de descomponer, y se
// restaura después.
//
// OJO CON EL SENTINELA, porque aquí había un comentario que MENTÍA: decía que un
// U+E000 literal en el texto «lo barrería el paso de símbolos». No es verdad. Su
// categoría Unicode es `Co` (Other, private use), que NO está en \p{P} ni en \p{S},
// así que PUNCT_SYM no lo toca: un U+E000 en la entrada sobrevive hasta el paso de
// restauración y sale convertido en "ñ". Medido, no deducido.
//
// No se cambia el CÓDIGO porque no hay impacto práctico —ningún comentario de
// Instagram trae caracteres de uso privado— y porque la idempotencia se mantiene:
// f(U+E000) = "ñ" y f("ñ") = "ñ". Se corrige el comentario para que describa lo
// que pasa de verdad.
const N_TILDE = 'ñ';
const N_TILDE_SENTINEL = String.fromCharCode(0xE000);

// Marcas diacríticas combinantes que deja NFD.
const COMBINING_MARKS = /[\u0300-\u036f]/g;

// Emojis. Property escapes con flag u, no rangos hardcodeados: un rango tipo
// \u{1F600}-\u{1F64F} deja fuera la mayor parte del set. Se incluyen los
// modificadores de tono, los indicadores regionales (banderas) y los conectores
// ZWJ (U+200D) / VS16 (U+FE0F), que NO son Extended_Pictographic y si no
// quedarían como residuo invisible.
const EMOJI = /[\p{Extended_Pictographic}\p{Emoji_Modifier}\p{Regional_Indicator}\u200d\ufe0f]/gu;

// Puntuación y símbolos. \p{N} (números) NO se toca a propósito: una keyword
// puede ser "2x1" — el 2x1 queda.
const PUNCT_SYM = /[\p{P}\p{S}]/gu;

/**
 * Normaliza texto de comentario o keyword a una forma canónica comparable.
 *
 * Orden de operaciones (importa):
 *   1. lowercase
 *   2. quitar diacríticos  — NFC -> proteger ñ -> NFD -> quitar marcas -> NFC
 *   3. quitar emojis
 *   4. quitar puntuación y símbolos
 *   5. colapsar espacios
 *   6. trim
 *
 * Por qué los diacríticos van ANTES de la puntuación: el paso usa
 * descomposición Unicode (NFD), que parte "á" en "a" + marca combinante. Si la
 * puntuación se quitara primero con una clase que incluya marcas, se romperían
 * caracteres compuestos a medio descomponer. Con NFD primero, cada carácter
 * llega entero al paso siguiente.
 *
 * @param {string} text
 * @returns {string} texto normalizado ('' si la entrada no es string)
 */
function normalizeCommentText(text) {
  if (typeof text !== 'string') return '';

  let s = text.toLowerCase();

  // 2. Diacríticos. El NFC inicial es para que una ñ que venga YA descompuesta
  //    ("n" + U+0303) se precomponga y el protector la alcance.
  s = s.normalize('NFC');
  s = s.split(N_TILDE).join(N_TILDE_SENTINEL);
  s = s.normalize('NFD').replace(COMBINING_MARKS, '').normalize('NFC');
  s = s.split(N_TILDE_SENTINEL).join(N_TILDE);

  // 3-4.
  s = s.replace(EMOJI, '');
  s = s.replace(PUNCT_SYM, ' ');

  // 5-6. Colapsar y trim. La puntuación se reemplazó por espacio (no por vacío)
  //      para que "info,precio" quede "info precio" y no "infoprecio".
  return s.replace(/\s+/g, ' ').trim();
}

/** Escapa metacaracteres para construir la RegExp del modo 'contains'. */
function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * ¿El texto normalizado contiene / equivale a la keyword?
 *
 * ESTA FUNCIÓN NORMALIZA LA KEYWORD POR SU CUENTA. Antes no lo hacía, y el
 * comentario que había aquí lo justificaba así: «normalizar dos veces escondería
 * una divergencia entre copias en vez de exponerla». El razonamiento era correcto
 * sobre un riesgo QUE YA CUBRE OTRA COSA — el gate de paridad byte a byte del
 * pre-commit (ver la cabecera de este fichero), que compara las dos copias en cada
 * commit y no depende de que ninguna keyword pase por aquí.
 *
 * Lo que NO cubría nadie era que un caller sencillamente no llamara a
 * `normalizeCommentText`. Y pasó: `CommentRulesSection.jsx` guardaba las keywords
 * crudas mientras `commentTemplates.js` las guardaba normalizadas. Resultado
 * medido: una regla activa con ["Precio","Valor",…], ocho comentarios reales,
 * `stats.matched: 0`, y ni un error en ningún log durante diez días. Un contrato
 * que un caller puede romper en silencio no es un contrato.
 *
 * Normalizar aquí es gratis y seguro: `normalizeCommentText` es IDEMPOTENTE
 * (verificado sobre 25 casos, 19 de los cuales cambian en la primera pasada), así
 * que una keyword ya normalizada atraviesa esto sin alterarse.
 *
 * `normalizedText` SÍ sigue exigiéndose normalizado: lo produce el runtime en el
 * mismo sitio donde llama a esta función, no es un dato persistido que alguien
 * pudiera haber escrito por otra ruta.
 *
 * @param {string} normalizedText  texto del comentario, ya normalizado
 * @param {string} keyword         keyword, cruda o normalizada — da igual
 * @param {'exact'|'contains'} mode
 * @returns {boolean}
 */
function matchKeyword(normalizedText, keyword, mode = 'contains') {
  if (typeof normalizedText !== 'string' || typeof keyword !== 'string') return false;

  const kw = normalizeCommentText(keyword);

  // Una keyword vacía no matchea NADA. Sin esta guarda, el modo 'contains'
  // construiría una RegExp que matchea en cualquier posición y una regla mal
  // guardada dispararía DMs a todo comentario que llegue.
  //
  // VA DESPUÉS DE NORMALIZAR, y el orden es el punto: una keyword NO vacía puede
  // normalizar a vacía — "!!!", un emoji suelto, sólo espacios. Con la comprobación
  // sobre el argumento crudo, esos casos se colaban y construían justo la RegExp
  // que esta guarda existe para impedir.
  if (kw === '') return false;

  if (mode === 'exact') return normalizedText === kw;

  // 'contains' con LÍMITE DE PALABRA, no substring: si no, "info" matchea
  // "informal" y "precio" matchea "impreciso".
  //
  // El borde izquierdo se consume (^ o un no-word-char) y el derecho va como
  // lookahead para no comerse el carácter siguiente, lo que rompería matches
  // solapados en textos como "info info".
  const re = new RegExp(
    '(?:^|[^' + WORD_CHARS + '])' + escapeRegExp(kw) + '(?![' + WORD_CHARS + '])',
    'u'
  );
  return re.test(normalizedText);
}

module.exports = {
  WORD_CHARS,
  normalizeCommentText,
  matchKeyword,
};
